/**
 * AllocationEditor - Premium, intuitive allocation editor
 *
 * Features:
 * - Smart currency input with real-time formatting
 * - Auto-fill remaining amount
 * - Visual balance indicators
 * - Bucket determines payer type (filtered automatically)
 * - Keyboard-friendly
 */

import { PlusIcon, SparklesIcon, TrashIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  AllocationBucket,
  CreateAllocation,
  PayerType,
} from '@/services/api/ledgerService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { BenefitType, CoverageSchema } from '@/services/types/coverage-types';

import { formatCurrencyPrecise, parseCurrency } from '../shared/formatUtils';

interface AllocationEditorProps {
  allocations: CreateAllocation[];
  billedAmount: string;
  onChange: (allocations: CreateAllocation[]) => void;
  matterId: string;
  expenseProviderId?: string;
  expenseProviderName?: string;
  providerHasLien?: boolean;
}

const ALLOCATION_BUCKETS: { value: AllocationBucket; label: string }[] = [
  { value: 'UNALLOCATED', label: 'Unallocated' },
  { value: 'PIP_PAYMENT', label: 'PIP Payment' },
  { value: 'MEDPAY_PAYMENT', label: 'MedPay Payment' },
  { value: 'HEALTH_INSURANCE_REIMBURSEMENT', label: 'Health Insurance' },
  { value: 'PROVIDER_DEFERRED_PAYMENT', label: 'Provider Deferred' },
  { value: 'CLIENT_SELF_PAYMENT', label: 'Client Payment' },
];

/**
 * Get the required payer type for a given bucket
 * Returns undefined if no payer type is needed (UNALLOCATED)
 */
const getRequiredPayerType = (
  bucket: AllocationBucket,
): PayerType | undefined => {
  switch (bucket) {
    case 'UNALLOCATED':
      return undefined;
    case 'PIP_PAYMENT':
    case 'MEDPAY_PAYMENT':
    case 'HEALTH_INSURANCE_REIMBURSEMENT':
      return 'COVERAGE';
    case 'PROVIDER_DEFERRED_PAYMENT':
      return 'PROVIDER';
    case 'CLIENT_SELF_PAYMENT':
      return 'CLIENT';
    default:
      return undefined;
  }
};

/**
 * Create an empty allocation row
 */
const createEmptyAllocation = (): CreateAllocation => ({
  allocation_bucket: 'UNALLOCATED',
  payer_type: undefined,
  payer_id: undefined,
  amount: '0.00',
});

export const AllocationEditor = ({
  allocations,
  billedAmount,
  onChange,
  matterId,
  expenseProviderId,
  expenseProviderName,
  providerHasLien = false,
}: AllocationEditorProps) => {
  // Fetch providers for the payer selector when payer_type is PROVIDER
  const { data: providers = [] } = useGetMedicalProvidersQuery(matterId, {
    skip: !matterId,
  });

  // Fetch coverages for the payer selector when payer_type is COVERAGE
  const { data: allCoverages = [] } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  // Filter coverages based on bucket type
  const getFilteredCoverages = useCallback(
    (bucket: AllocationBucket): CoverageSchema[] => {
      if (bucket === 'PIP_PAYMENT') {
        return allCoverages.filter((c) =>
          c.benefits?.some(
            (b) => b.benefit_type === BenefitType.PERSONAL_INJURY_PROTECTION,
          ),
        );
      }
      if (bucket === 'MEDPAY_PAYMENT') {
        return allCoverages.filter((c) =>
          c.benefits?.some(
            (b) => b.benefit_type === BenefitType.MEDICAL_PAYMENTS,
          ),
        );
      }
      if (bucket === 'HEALTH_INSURANCE_REIMBURSEMENT') {
        return allCoverages.filter((c) => c.category === 'FIRST_PARTY_HEALTH');
      }
      return allCoverages;
    },
    [allCoverages],
  );

  // Calculate totals
  const billedAmountNum = parseCurrency(billedAmount);
  const allocatedTotal = useMemo(() => {
    return allocations.reduce((sum, alloc) => {
      return sum + parseCurrency(alloc.amount);
    }, 0);
  }, [allocations]);

  const remaining = billedAmountNum - allocatedTotal;
  const isBalanced = Math.abs(remaining) < 0.01;
  const hasRemaining = remaining > 0.01;

  // Check if all allocations are valid (have payer when needed)
  const allAllocationsValid = useMemo(() => {
    return allocations.every((alloc) => {
      // UNALLOCATED and CLIENT don't need a payer
      if (
        alloc.allocation_bucket === 'UNALLOCATED' ||
        alloc.allocation_bucket === 'CLIENT_SELF_PAYMENT'
      ) {
        return true;
      }
      // All others need a payer_id
      return !!alloc.payer_id;
    });
  }, [allocations]);

  // Handlers
  const handleAddAllocation = useCallback(() => {
    const newAlloc = createEmptyAllocation();
    // If there's remaining amount, pre-fill it
    if (hasRemaining) {
      newAlloc.amount = remaining.toFixed(2);
    }
    onChange([...allocations, newAlloc]);
  }, [allocations, onChange, remaining, hasRemaining]);

  const handleRemoveAllocation = useCallback(
    (index: number) => {
      const updated = allocations.filter((_, i) => i !== index);
      onChange(updated);
    },
    [allocations, onChange],
  );

  const handleAutoAllocate = useCallback(() => {
    // Reset to single unallocated row with full amount
    onChange([
      {
        allocation_bucket: 'UNALLOCATED',
        payer_type: undefined,
        payer_id: undefined,
        amount: billedAmount || '0',
      },
    ]);
  }, [onChange, billedAmount]);

  const handleFillRemaining = useCallback(
    (index: number) => {
      if (!hasRemaining) return;
      const updated = allocations.map((alloc, i) => {
        if (i !== index) return alloc;
        return {
          ...alloc,
          amount: remaining.toFixed(2),
        };
      });
      onChange(updated);
    },
    [allocations, onChange, remaining, hasRemaining],
  );

  const handleAllocationChange = useCallback(
    (index: number, field: keyof CreateAllocation, value: string | null) => {
      const updated = allocations.map((alloc, i) => {
        if (i !== index) return alloc;
        const newAlloc = { ...alloc };

        if (field === 'allocation_bucket') {
          newAlloc.allocation_bucket = value as AllocationBucket;
          // Auto-set payer type based on bucket (bucket determines payer type!)
          const requiredPayerType = getRequiredPayerType(
            newAlloc.allocation_bucket,
          );
          newAlloc.payer_type = requiredPayerType;
          // For provider deferred, auto-set to expense provider
          if (
            newAlloc.allocation_bucket === 'PROVIDER_DEFERRED_PAYMENT' &&
            expenseProviderId
          ) {
            newAlloc.payer_id = expenseProviderId;
          } else {
            newAlloc.payer_id = undefined;
          }
        } else if (field === 'payer_type') {
          // This shouldn't normally be called since bucket determines type,
          // but keep it for edge cases
          newAlloc.payer_type = value as PayerType;
          newAlloc.payer_id = undefined;
        } else if (field === 'payer_id') {
          newAlloc.payer_id = value || undefined;
        } else if (field === 'amount') {
          // Just store the value directly - no formatting on change
          newAlloc.amount = value || '0';
        }

        return newAlloc;
      });
      onChange(updated);
    },
    [allocations, onChange, expenseProviderId],
  );

  const renderPayerSelector = (allocation: CreateAllocation, index: number) => {
    // Provider Deferred is always the expense provider
    if (allocation.allocation_bucket === 'PROVIDER_DEFERRED_PAYMENT') {
      return (
        <div className="text-sm text-slate-600 truncate">
          {expenseProviderName || 'Select provider above'}
        </div>
      );
    }

    // Bucket determines payer type, so we know what to show
    if (allocation.payer_type === 'PROVIDER') {
      return (
        <Select
          value={allocation.payer_id ?? ''}
          onValueChange={(value) =>
            handleAllocationChange(index, 'payer_id', value)
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select provider" />
          </SelectTrigger>
          <SelectContent>
            {providers.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-gray-500">
                No providers available
              </div>
            ) : (
              providers.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  <span className="truncate">
                    {provider.name ?? 'Unnamed Provider'}
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      );
    }

    if (allocation.payer_type === 'COVERAGE') {
      const filteredCoverages = getFilteredCoverages(
        allocation.allocation_bucket,
      );
      return (
        <Select
          value={allocation.payer_id ?? ''}
          onValueChange={(value) =>
            handleAllocationChange(index, 'payer_id', value)
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select coverage" />
          </SelectTrigger>
          <SelectContent>
            {filteredCoverages.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-gray-500">
                No matching coverage found
              </div>
            ) : (
              filteredCoverages.map((coverage) => (
                <SelectItem key={coverage.id} value={coverage.id}>
                  <span className="truncate">
                    {coverage.carrier?.name ?? 'Unknown Carrier'}
                    {coverage.policy_type && ` - ${coverage.policy_type}`}
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      );
    }

    if (allocation.payer_type === 'CLIENT') {
      return <div className="text-sm text-slate-600 truncate">Client</div>;
    }

    return <span className="text-sm text-slate-400">—</span>;
  };

  const getRemainingColor = () => {
    if (isBalanced) return 'text-green-600';
    if (remaining > 0) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-900">
          Who Paid for This?
        </span>
        <div className="flex items-center gap-2 -mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAutoAllocate}
            className="h-8 text-xs text-slate-600 hover:text-slate-900"
          >
            Reset
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddAllocation}
            className="h-8 text-xs"
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Add
          </Button>
        </div>
      </div>

      {/* Allocation Rows - Streamlined */}
      {allocations.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          Click &quot;Add Row&quot; to allocate payment sources
        </div>
      ) : (
        <div className="space-y-1.5">
          {allocations.map((allocation, index) => {
            const isUnallocated =
              allocation.allocation_bucket === 'UNALLOCATED';
            const canFillRemaining =
              hasRemaining && index === allocations.length - 1;

            return (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50 transition-colors"
              >
                {/* Bucket */}
                <div className="col-span-4">
                  <Select
                    value={allocation.allocation_bucket}
                    onValueChange={(value) =>
                      handleAllocationChange(index, 'allocation_bucket', value)
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALLOCATION_BUCKETS.map((bucket) => {
                        const isProviderDeferred =
                          bucket.value === 'PROVIDER_DEFERRED_PAYMENT';
                        const disabled = isProviderDeferred && !providerHasLien;

                        return (
                          <SelectItem
                            key={bucket.value}
                            value={bucket.value}
                            disabled={disabled}
                            className={disabled ? 'opacity-40' : ''}
                          >
                            {bucket.label}
                            {disabled && (
                              <span className="ml-2 text-xs text-slate-400">
                                (requires lien)
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payer */}
                {isUnallocated ? (
                  <div className="col-span-4 flex items-center text-sm text-slate-400">
                    —
                  </div>
                ) : (
                  <div className="col-span-4">
                    {renderPayerSelector(allocation, index)}
                  </div>
                )}

                {/* Amount */}
                <div className="col-span-3 relative">
                  <div className="relative group">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                      $
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-7 pr-10 tabular-nums text-right"
                      value={allocation.amount}
                      onChange={(e) =>
                        handleAllocationChange(index, 'amount', e.target.value)
                      }
                    />
                    {canFillRemaining && (
                      <button
                        type="button"
                        onClick={() => handleFillRemaining(index)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                        title={`Fill ${formatCurrencyPrecise(remaining)}`}
                      >
                        <SparklesIcon className="h-4 w-4 text-blue-500" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="col-span-1 flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveAllocation(index)}
                    className="h-8 w-8 text-slate-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center justify-between pt-3 border-t text-xs">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-slate-500">Billed: </span>
            <span className="font-semibold text-slate-900 tabular-nums">
              {formatCurrencyPrecise(billedAmount)}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Allocated: </span>
            <span className="font-semibold text-slate-900 tabular-nums">
              {formatCurrencyPrecise(allocatedTotal)}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Remaining: </span>
            <span
              className={`font-semibold tabular-nums ${getRemainingColor()}`}
            >
              {formatCurrencyPrecise(remaining)}
            </span>
          </div>
        </div>
        {(() => {
          if (!allAllocationsValid) {
            return (
              <span className="text-xs font-medium text-red-600">
                Select payers
              </span>
            );
          }
          if (!isBalanced) {
            return (
              <span className={`text-xs font-medium ${getRemainingColor()}`}>
                {remaining > 0 ? 'Under-allocated' : 'Over-allocated'}
              </span>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
};
